import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, ChevronDown, Filter, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useIsDarkMode } from "@/hooks/useIsDarkMode";

interface FilterOptions {
  country?: string;
  dateFilter?: "all" | "today" | "yesterday" | "specific";
  specificDate?: Date;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FilterOptions) => void;
  currentFilters: FilterOptions;
}

const COUNTRIES = [
  { value: "all", label: "All Countries", flag: "🌍" },
  { value: "us", label: "United States", flag: "🇺🇸" },
  { value: "gb", label: "United Kingdom", flag: "🇬🇧" },
  { value: "ca", label: "Canada", flag: "🇨🇦" },
  { value: "au", label: "Australia", flag: "🇦🇺" },
  { value: "in", label: "India", flag: "🇮🇳" },
  { value: "de", label: "Germany", flag: "🇩🇪" },
  { value: "fr", label: "France", flag: "🇫🇷" },
  { value: "jp", label: "Japan", flag: "🇯🇵" },
  { value: "br", label: "Brazil", flag: "🇧🇷" },
  { value: "cn", label: "China", flag: "🇨🇳" },
  { value: "ru", label: "Russia", flag: "🇷🇺" },
  { value: "it", label: "Italy", flag: "🇮🇹" },
  { value: "es", label: "Spain", flag: "🇪🇸" },
  { value: "mx", label: "Mexico", flag: "🇲🇽" },
  { value: "ar", label: "Argentina", flag: "🇦🇷" },
  { value: "za", label: "South Africa", flag: "🇿🇦" },
  { value: "kr", label: "South Korea", flag: "🇰🇷" },
  { value: "sg", label: "Singapore", flag: "🇸🇬" },
  { value: "nl", label: "Netherlands", flag: "🇳🇱" },
  { value: "se", label: "Sweden", flag: "🇸🇪" },
  { value: "no", label: "Norway", flag: "🇳🇴" },
  { value: "ch", label: "Switzerland", flag: "🇨🇭" },
  { value: "at", label: "Austria", flag: "🇦🇹" },
  { value: "be", label: "Belgium", flag: "🇧🇪" },
];

export const FilterModal = ({ isOpen, onClose, onApplyFilters, currentFilters }: FilterModalProps) => {
  const [filters, setFilters] = useState<FilterOptions>(currentFilters);
  const [showCalendar, setShowCalendar] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const isDarkMode = useIsDarkMode();
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCountryDropdownOpen(false);
      setShowCalendar(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!countryDropdownOpen) return;
      const target = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setCountryDropdownOpen(false);
      }
    };

    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [countryDropdownOpen]);

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setFilters((prev) => ({
        ...prev,
        dateFilter: "specific",
        specificDate: date,
      }));
    }
    setShowCalendar(false);
  };

  const selectedCountry =
    COUNTRIES.find((country) => country.value === (filters.country ?? "all")) ?? COUNTRIES[0];

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={handleOverlayClick}
    >
      <div
        className={cn(
          "relative w-full max-w-md rounded-2xl border px-5 py-6 shadow-2xl transition-colors duration-200",
          isDarkMode
            ? "bg-[#1e1e1e] border-white/10 text-slate-100"
            : "bg-white border-slate-200 text-slate-900"
        )}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold">
              <Filter className="h-5 w-5 text-primary" />
              <span>Filter News</span>
            </div>
            <p
              className={cn(
                "mt-1 text-sm transition-colors duration-200",
                isDarkMode ? "text-slate-300" : "text-slate-500"
              )}
            >
              Filter articles by country and date
            </p>
          </div>
          <button
            type="button"
            aria-label="Close filter modal"
            onClick={onClose}
            className={cn(
              "rounded-full p-2 transition-colors duration-200",
              isDarkMode
                ? "hover:bg-white/10 text-slate-300"
                : "hover:bg-slate-100 text-slate-500"
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <div ref={dropdownRef} className="relative">
              <button
                id="country"
                type="button"
                onClick={() => setCountryDropdownOpen((prev) => !prev)}
                className={cn(
                  "flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-200",
                  isDarkMode
                    ? "bg-[#1f1f1f] border-white/10 text-slate-100"
                    : "bg-white border-slate-200 text-slate-900"
                )}
              >
                <span className="flex items-center gap-3">
                  <span className="text-lg leading-none">{selectedCountry.flag}</span>
                  <span>{selectedCountry.label}</span>
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 transition-transform duration-200",
                    countryDropdownOpen && "rotate-180"
                  )}
                />
              </button>

              {countryDropdownOpen && (
                <div
                  className={cn(
                    "absolute left-0 right-0 top-full z-10 mt-2 rounded-xl border shadow-xl",
                    isDarkMode
                      ? "border-white/10 bg-[#121212]"
                      : "border-slate-200 bg-white"
                  )}
                >
                  <div className="max-h-60 overflow-y-auto py-2">
                    {COUNTRIES.map((country) => {
                      const isSelected = country.value === (filters.country ?? "all");
                      return (
                        <button
                          key={country.value}
                          type="button"
                          className={cn(
                            "flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors duration-150",
                            isDarkMode
                              ? "hover:bg-white/10"
                              : "hover:bg-slate-100",
                            isSelected &&
                              (isDarkMode ? "bg-white/10 text-white" : "bg-slate-100 text-slate-900")
                          )}
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              country: country.value === "all" ? undefined : country.value,
                            }));
                            setCountryDropdownOpen(false);
                          }}
                        >
                          <span className="text-base leading-none">{country.flag}</span>
                          <span>{country.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <Label>Date</Label>
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button
                  variant={filters.dateFilter === "all" || !filters.dateFilter ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, dateFilter: "all", specificDate: undefined }))
                  }
                  className="flex-1"
                >
                  All
                </Button>
                <Button
                  variant={filters.dateFilter === "today" ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, dateFilter: "today", specificDate: undefined }))
                  }
                  className="flex-1"
                >
                  Today
                </Button>
                <Button
                  variant={filters.dateFilter === "yesterday" ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, dateFilter: "yesterday", specificDate: undefined }))
                  }
                  className="flex-1"
                >
                  Yesterday
                </Button>
              </div>

              <Button
                variant={filters.dateFilter === "specific" ? "default" : "outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  filters.dateFilter !== "specific" && "text-muted-foreground"
                )}
                onClick={() => {
                  setShowCalendar((prev) => !prev);
                  setFilters((prev) => ({ ...prev, dateFilter: "specific" }));
                }}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {filters.dateFilter === "specific" && filters.specificDate
                  ? format(filters.specificDate, "PPP")
                  : "Pick a specific date"}
              </Button>

              {showCalendar && (
                <div
                  className={cn(
                    "mt-3 rounded-xl border p-3 absolute top-0 -left-2 mx-auto",
                    isDarkMode ? "border-white/10 bg-[#121212]" : "border-slate-200 bg-slate-50"
                  )}
                >
                  <Calendar
                    mode="single"
                    selected={filters.specificDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => date > new Date()}
                    className={cn(
                      "pointer-events-auto",
                      isDarkMode ? "text-slate-100" : "text-slate-900"
                    )}
                  />
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowCalendar(false);
                        if (!filters.specificDate) {
                          setFilters((prev) => ({ ...prev, dateFilter: undefined }));
                        }
                      }}
                    >
                      Close
                    </Button>
                    {filters.specificDate && (
                      <Button size="sm" onClick={() => setShowCalendar(false)}>
                        Save Date
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={handleApply}>Done</Button>
        </div>
      </div>
    </div>
  );
}
;